<script>
import { mapGetters } from 'vuex'

import { apiSecond } from '@/boot/axios'

import AqKpi from '@/components/aqua/AqKpi.vue'
import AqPageHeader from '@/components/aqua/AqPageHeader.vue'
import AqStatus from '@/components/aqua/AqStatus.vue'
import SelectFish from '@/components/catalog/SelectFish.vue'
import GrownFishModal from '@/views/account/logs/GrownManagement/GrownFishModal.vue'

export default {
	name: 'GrownCreatePage',
	components: {
		AqKpi,
		AqPageHeader,
		AqStatus,
		SelectFish,
		GrownFishModal,
	},
	props: {
		statusPage: {
			type: String,
			default: 'create',
		},
	},
	data() {
		return {
			isNotEditable: true,
			application: null,
			catch_at: null,
			fishes: [],
			selectedFish: null,
			errorMessage: {
				status: null,
				messages: null,
				text: null,
			},
			message: {
				status: null,
				text: null,
			},
			formData: null,
			dataEcp: null,
			signing: false,
			rejectReason: '',
		}
	},
	computed: {
		...mapGetters(['_getUserObject']),
		isTir() {
			return this._getUserObject?.role?.name === 'tir'
		},
		statusKey() {
			return this.application?.status || 'new'
		},
		canDecide() {
			return this.isTir && this.statusKey === 'subject_signed'
		},
		headerTitle() {
			if (!this.catch_at) return this.$t('titles.grownJournals')
			return this.$t('aqua.ui.grown.record_from', { date: this.$options.filters.formatOnlyDate(this.application?.catch_at || this.catch_at) })
		},
		plot() {
			return this.application?.plot || null
		},
		totalGrown() {
			return this.fishes.reduce((sum, fish) => sum + (Number(fish.actual_production_by_species) || 0), 0)
		},
		rejectLength() {
			return this.rejectReason.trim().length
		},
	},
	methods: {
		closeModal() {
			this.selectedFish = null
			this.$modal.hide('GrownFishModal')
		},
		openModal(fish) {
			this.selectedFish = fish
			this.$modal.show('GrownFishModal')
		},
		catchError(error) {
			if (error?.response?.status == 500) {
				this.errorMessage.status = 500
				this.errorMessage.text = this.$t('system_message.500')
			}
			if (error?.response?.status == 401) {
				this.errorMessage.status = 401
				this.errorMessage.text = error.response.data.error_message
				this.$router.push('/' + this.$i18n.locale + '/login')
			}
			if (error?.response?.status == 422) {
				this.errorMessage.status = 422
				this.errorMessage.text = error.response.data.error_message
			}
			if (error?.response?.status == 403) {
				this.errorMessage.status = 403
				this.errorMessage.text = error?.response?.data?.message
			}
		},
		async apiGet() {
			if (!this.$route.params.userId) return
			try {
				const response = await apiSecond.get('v1/grown/' + this.$route.params.userId)
				if (response.data.data) {
					this.application = response.data.data
					if (this.application.catch_at) {
						this.catch_at = String(this.application.catch_at).slice(0, 10)
					}
					if (this.application.fishs && this.application.fishs.length) {
						this.fishes = this.application.fishs.map((e, ind) => {
							return {
								id: new Date().getTime() + '-' + ind,
								type: {
									id: e.fish_id,
									title: e.fishName,
								},
								_id: e.id,
								quantity: e.value,
								water_supply_source_id: e.water_supply_source || e.water_supply_source_id,
								water_supply_source_volume: e.water_supply_source_volume,
								design_capacity: e.design_capacity,
								actual_production_by_species: e.actual_production_by_species,
								sold_volume: e.sold_volume,
								juvenile_production_by_age: e.juvenile_production_by_age,
								juvenile_sold: e.juvenile_sold,
								broodstock_production: e.broodstock_production,
								broodstock_sold: e.broodstock_sold,
								feed_consumption_tons: e.feed_consumption_tons,
								feed_info: e.feed_info,
							}
						})
					}
				}
			} catch (error) {
				this.catchError(error)
			}
		},
		async startEcpSigning() {
			try {
				this.signing = true
				this.dataEcp = null
				this.errorMessage.status = null
				this.errorMessage.text = null

				// Check if NCALayer is available
				if (!this.isNCALayerAvailable()) {
					throw new Error('NCALayer not available')
				}

				const { NCALayerClientIns } = await import('@/shared/services/nca')
				const request = {
					module: 'kz.gov.pki.knca.commonUtils',
					method: 'signXml',
					args: [
						'PKCS12',
						'SIGNATURE',
						'<login><sessionid>caacda70-fd36-45ed-8d94-45a88890f83a</sessionid></login>',
						'',
						'',
					],
				}

				const xml = await NCALayerClientIns.request(request)
				this.dataEcp = xml
				if (xml) {
					await this.sendEcpSignature(xml)
				}
			} catch (error) {
				this.handleEcpError(error)
			} finally {
				this.signing = false
			}
		},
		isNCALayerAvailable() {
			// Check if we're in a secure context (HTTPS or localhost)
			if (!window.isSecureContext && location.hostname !== 'localhost') {
				return false
			}
			// Check if WebSocket is supported
			if (!window.WebSocket) {
				return false
			}
			return true
		},
		handleEcpError(error) {
			let message = this.$t('system_message.error_occurred')
			let status = 500

			// Handle specific error types
			if (error?.message) {
				const errorMessage = error.message.toLowerCase()
				if (errorMessage.includes('ncalayer not available') || errorMessage.includes('websocket')) {
					message = this.$t('system_message.nca_not_available')
					status = 503
				} else if (errorMessage.includes('timeout')) {
					message = this.$t('system_message.nca_timeout')
					status = 408
				} else if (errorMessage.includes('certificate') || errorMessage.includes('cert')) {
					message = this.$t('system_message.nca_certificate_error')
					status = 400
				} else if (errorMessage.includes('connection')) {
					message = this.$t('system_message.nca_connection_failed')
					status = 503
				} else if (errorMessage.includes('ncalayer')) {
					message = this.$t('system_message.nca_not_running')
					status = 503
				} else if (errorMessage.includes('user cancelled') || errorMessage.includes('cancelled')) {
					message = this.$t('system_message.nca_user_cancelled')
					status = 400
				} else if (errorMessage.includes('invalid response')) {
					message = this.$t('system_message.nca_invalid_response')
					status = 500
				}
			}

			// Handle HTTP errors from API
			if (error?.response) {
				status = error.response.status
				if (error.response.data?.message) {
					message = error.response.data.message
				} else if (error.response.status === 401) {
					message = this.$t('system_message.unauthorized')
				} else if (error.response.status === 403) {
					message = this.$t('system_message.forbidden')
				} else if (error.response.status === 404) {
					message = this.$t('system_message.not_found')
				} else if (error.response.status === 422) {
					message = this.$t('system_message.validation_error')
				} else if (error.response.status >= 500) {
					message = this.$t('system_message.server_error')
				}
			}

			this.errorMessage.status = status
			this.errorMessage.text = message
			this.$toast.error(message)
		},
		async sendEcpSignature(xml) {
			try {
				const formData = new FormData()
				formData.append('xml', xml)
				formData.append('document_id', this.$route.params.userId)
				const response = await apiSecond.post(`/v1/grown/sign`, formData)

				this.$toast.success(
					response.data.message || this.$t('reporting.notifications.signed_success'),
				)

				this.message.status = 200
				this.message.text =
					response.data.message || this.$t('reporting.notifications.signed_success')
				// Refresh application data to show updated signed status
				await this.apiGet()
			} catch (error) {
				// Show error toast
				const errorMessage =
					error?.response?.data?.message || this.$t('system_message.error_occurred')
				this.$toast.error(errorMessage)

				this.errorMessage.status = error?.response?.status || 500
				this.errorMessage.text = errorMessage
			}
		},
		async sendReject() {
			const reason = this.rejectReason.trim()
			if (reason.length < 5) {
				this.$toast.error(this.$t('grown_journal.reject_title'))
				return
			}
			try {
				const response = await apiSecond.post(`/v1/grown/${this.$route.params.userId}/reject`, { reason })
				this.$modal.hide('GrownRejectModal')
				this.rejectReason = ''
				this.$toast.success(response.data.message || this.$t('grown_journal.rejected'))
				await this.apiGet()
			} catch (error) {
				this.$toast.error(
					error?.response?.data?.message ||
						error?.response?.data?.error_message ||
						this.$t('system_message.error_occurred'),
				)
			}
		},
		async sendEcpKey(xml) {
			try {
				const formData = new FormData()
				formData.append('xml', xml)
				await apiSecond.post(`/v1/grown/sign/${this.$route.params.id}`, formData)
				this.message.status = 200
				this.message.text = this.$t('reporting.notifications.signed_success')
				await this.apiGet()
			} catch (error) {
				this.errorMessage.status = error?.response?.status || 500
				this.errorMessage.text =
					error?.response?.data?.message || this.$t('system_message.error_occurred')
			}
		},
		validateFirm() {
			this.errorMessage.status = null
			this.errorMessage.messages = null
			this.errorMessage.text = null

			let valid = false
			let messages = {}

			messages.fish = []
			this.fishes.forEach((element) => {
				if (element.type == null || element.quantity == 0) {
					valid = true
					messages.fish[element.id] = this.$t('system_message.required_message')
				}
			})

			if (valid) {
				this.errorMessage.messages = messages
				this.errorMessage.status = 422
				this.errorMessage.text = this.$t('system_message.form_error')
			} else {
				this.onSubmit()
			}
		},
		onSubmit() {
			this.message.status = null
			this.message.text = null

			const formData = new FormData()

			if (this.$route.params.id) {
				formData.append('grown_id', this.$route.params.id)
			}
			formData.append('user_id', this.$store.state.userObject.id)
			formData.append('catch_at', this.catch_at)
			this.fishes.find((fish, ind) => {
				if (fish._id) {
					formData.append(`fish[${ind}][id]`, fish._id)
				}
				formData.append(`fish[${ind}][fish_id]`, fish.type?.id)

				formData.append(`fish[${ind}][value]`, String(Number(fish.quantity)))
				formData.append(`fish[${ind}][design_capacity]`, String(Number(fish.design_capacity)))
				formData.append(
					`fish[${ind}][actual_production_by_species]`,
					String(Number(fish.actual_production_by_species)),
				)
				formData.append(`fish[${ind}][sold_volume]`, String(Number(fish.sold_volume)))
				formData.append(
					`fish[${ind}][juvenile_production_by_age]`,
					String(Number(fish.juvenile_production_by_age)),
				)
				formData.append(`fish[${ind}][juvenile_sold]`, String(Number(fish.juvenile_sold)))
				formData.append(
					`fish[${ind}][broodstock_production]`,
					String(Number(fish.broodstock_production)),
				)
				formData.append(`fish[${ind}][broodstock_sold]`, String(Number(fish.broodstock_sold)))
				formData.append(
					`fish[${ind}][feed_consumption_tons]`,
					String(Number(fish.feed_consumption_tons)),
				)
				formData.append(`fish[${ind}][feed_info]`, String(Number(fish.feed_info)))
			})

			apiSecond
				.post(this.$route.params.id ? '/v1/grown/update' : '/v1/grown/create', formData)
				.then((response) => {
					if (response.status == 200) {
						this.message.status = 200
						this.message.text = response.data.message
						this.$router.push({
							path: '/' + this.$i18n.locale + '/account/logs/grown/' + response.data.data.id,
						})
					}
				})
				.catch(this.catchError.bind(this))
		},
		formatNum(value) {
			if (value === null || value === undefined || value === '') return '—'
			const number = Number(value)
			return Number.isNaN(number) ? value : number.toLocaleString('ru-RU', { maximumFractionDigits: 3 })
		},
		fishPercent(fish) {
			const design = Number(fish.design_capacity)
			if (!design) return null
			return Math.round(((Number(fish.actual_production_by_species) || 0) / design) * 100)
		},
		async confirmDelete() {
			const confirmed = await this.$dialog.confirm({
				title: this.$t('system_message.confirm_delete'),
				confirmText: this.$t('button.delete'),
			})

			if (confirmed) {
				try {
					await apiSecond.delete(`/v1/grown/destroy/${this.$route.params.id}`)
					this.message.status = 200
					this.message.text = this.$t('system_message.deleted_successfully')
					setTimeout(() => {
						this.$router.push({
							path: '/' + this.$i18n.locale + '/account/logs/grown',
						})
					}, 1500)
				} catch (error) {
					this.errorMessage.status = error?.response?.status || 500
					this.errorMessage.text =
						error?.response?.data?.message || this.$t('system_message.error_occurred')
				}
			}
		},
	},
	beforeMount() {
		this.apiGet()
	},
	beforeCreate() {
		if (!localStorage.token) {
			this.$router.push('/ru/login')
		}
	},
}
</script>

<template>
	<div
		class="wrap__content aq-page"
		v-bind:class="{
			'wrap__content--preloader': !$store.state.userObject,
		}"
	>
		<v-preloader v-if="!$store.state.userObject" :message="errorMessage"></v-preloader>

		<div v-if="$store.state.userObject" class="container">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="['ReadGrownAllManagement1']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content aq-shell">
							<AqPageHeader
								:title="headerTitle"
								:eyebrow="$t('sidebar.aqua_report')"
								:back="'/' + $i18n.locale + '/account/read-grown-all'"
								:back-label="$t('aqua.ui.grown.to_records')"
							>
								<template v-if="application" #meta>
									<AqStatus :value="statusKey" :label="$t('statusses.readGrownAllTir.' + statusKey)" />
									<span v-if="plot" class="aq-meta">{{ plot.title }}</span>
									<span v-if="plot && plot.region" class="aq-meta">{{ plot.region.title }}</span>
								</template>
							</AqPageHeader>

							<div class="aq-layout">
								<div class="aq-layout__main">
									<div v-if="application && statusKey === 'rejected' && application.reject_reason" class="aq-alert aq-alert--danger">
										<svg class="aq-alert__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<path d="M4 5h16v11H8l-4 4z" />
										</svg>
										<div>
											<strong>{{ $t('grown_journal.reject_reason') }}</strong>
											<p class="aq-alert__text">{{ application.reject_reason }}</p>
										</div>
									</div>

									<section class="aq-card">
										<div class="aq-card__head">
											<span class="aq-step">1</span>
											<div>
												<h2 class="aq-card__title">{{ $t('aqua.ui.grown.section_place') }}</h2>
												<p class="aq-card__subtitle">{{ $t('aqua.ui.grown.section_place_hint') }}</p>
											</div>
										</div>
										<dl class="aq-card__body aq-grid aq-facts">
											<div>
												<dt>{{ $t('labels.reportPeriod') }}</dt>
												<dd>{{ application && application.catch_at ? $options.filters.formatOnlyDate(application.catch_at) : '—' }}</dd>
											</div>
											<div>
												<dt>{{ $t('inputs.region') }}</dt>
												<dd>{{ plot && plot.region ? plot.region.title : '—' }}</dd>
											</div>
											<div>
												<dt>{{ $t('handbooks.Район') }}</dt>
												<dd>{{ plot && plot.district ? plot.district.title : '—' }}</dd>
											</div>
											<div>
												<dt>{{ $t('inputs.pond_name') }}</dt>
												<dd>{{ plot && plot.reservoir ? plot.reservoir.title : '—' }}</dd>
											</div>
											<div class="aq-facts__wide">
												<dt>{{ $t('handbooks.Участки') }}</dt>
												<dd>{{ plot ? plot.title : '—' }}</dd>
											</div>
										</dl>
									</section>

									<section class="aq-card">
										<div class="aq-card__head">
											<span class="aq-step">2</span>
											<div>
												<h2 class="aq-card__title">{{ $t('labels.objectAqua') }}</h2>
												<p class="aq-card__subtitle">{{ $t('aqua.ui.grown.fishes_count', { count: fishes.length }) }}</p>
											</div>
										</div>
										<div class="aq-card__body">
											<div v-if="fishes.length" class="aq-fishes">
												<article v-for="(fish, fishInd) in fishes" :key="fish.id" class="aq-fish">
													<header class="aq-fish__head">
														<span class="aq-fish__num">{{ fishInd + 1 }}</span>
														<strong class="aq-fish__title">{{ fish.type && fish.type.title ? fish.type.title : '—' }}</strong>
													</header>
													<div class="aq-fish__progress">
														<div class="aq-fish__progress-label">
															<span>{{ $t('aqua.ui.grown.grown_of_capacity') }}</span>
															<strong>
																{{ formatNum(fish.actual_production_by_species) }}
																<template v-if="fish.design_capacity">/ {{ formatNum(fish.design_capacity) }}</template>
																{{ $t('aqua.ui.grown.unit_t') }}
															</strong>
														</div>
														<div
															v-if="fishPercent(fish) !== null"
															class="aq-bar"
															:class="{ 'aq-bar--over': fishPercent(fish) > 100 }"
															role="progressbar"
															:aria-valuenow="fishPercent(fish)"
															aria-valuemin="0"
															aria-valuemax="100"
														>
															<span :style="{ width: Math.min(fishPercent(fish), 100) + '%' }"></span>
														</div>
														<span v-if="fishPercent(fish) > 100" class="aq-fish__warn">{{ $t('aqua.ui.grown.over_capacity') }}</span>
													</div>
													<dl class="aq-fish__stats">
														<div>
															<dt>{{ $t('aqua.ui.grown.sold') }}</dt>
															<dd>{{ formatNum(fish.sold_volume) }}</dd>
														</div>
														<div>
															<dt>{{ $t('aqua.ui.grown.juvenile') }}</dt>
															<dd>{{ formatNum(fish.juvenile_production_by_age) }}</dd>
														</div>
														<div>
															<dt>{{ $t('aqua.ui.grown.feed') }}</dt>
															<dd>{{ formatNum(fish.feed_consumption_tons) }}</dd>
														</div>
													</dl>
													<button type="button" class="aq-fish__more" @click.stop.prevent="openModal(fish)">
														{{ $t('tooltip.look') }}
														<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
															<path d="M9 5l7 7-7 7" />
														</svg>
													</button>
												</article>
											</div>
											<p v-else class="aq-muted-note">{{ $t('aqua.ui.grown.fishes_empty') }}</p>
										</div>
									</section>
								</div>

								<aside class="aq-layout__side">
									<div class="aq-card aq-panel">
										<div v-if="application" class="aq-panel__status">
											<span class="aq-panel__label">{{ $t('headers.status') }}</span>
											<AqStatus :value="statusKey" :label="$t('statusses.readGrownAllTir.' + statusKey)" />
											<ul class="aq-timeline">
												<li v-if="application.signed_at">
													<span>{{ $t('aqua.ui.grown.signed_at') }}</span>
													<strong>{{ application.signed_at | formatDate }}</strong>
												</li>
												<li v-if="application.tir_signed_at">
													<span>{{ $t('grown_journal.approved') }}</span>
													<strong>{{ application.tir_signed_at | formatDate }}</strong>
												</li>
												<li v-if="application.rejected_at && statusKey === 'rejected'">
													<span>{{ $t('grown_journal.rejected') }}</span>
													<strong>{{ application.rejected_at | formatDate }}</strong>
												</li>
											</ul>
										</div>

										<AqKpi
											v-if="fishes.length"
											:label="$t('aqua.ui.grown.total_grown')"
											:value="formatNum(totalGrown)"
											:unit="$t('aqua.ui.grown.unit_t')"
											:hint="$t('aqua.ui.grown.fishes_count', { count: fishes.length })"
										/>

										<AqKpi
											v-if="application && application.warehouse"
											tone="success"
											:label="$t('aqua.ui.grown.available_volume')"
											:value="formatNum(application.warehouse.available_volume)"
											:unit="$t('aqua.ui.grown.unit_t')"
											:hint="`${$t('aqua.ui.grown.nikad')}: ${application.warehouse.nikad}`"
										/>

										<div v-if="canDecide" class="aq-panel__actions">
											<span class="aq-panel__label">{{ $t('aqua.ui.grown.decision') }}</span>
											<button
												type="button"
												class="aq-btn aq-btn--primary aq-btn--block"
												:disabled="signing"
												@click.prevent="startEcpSigning"
											>
												<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
													<path d="M5 12.5l4.5 4.5L19 7" />
												</svg>
												<span v-if="!signing">{{ $t('grown_journal.approve') }}</span>
												<span v-else>{{ $t('button.signing') }}…</span>
											</button>
											<button
												type="button"
												class="aq-btn aq-btn--danger-outline aq-btn--block"
												:disabled="signing"
												@click.prevent="$modal.show('GrownRejectModal')"
											>
												{{ $t('grown_journal.reject') }}
											</button>
										</div>
										<p v-else-if="statusKey === 'subject_signed'" class="aq-panel__hint">
											{{ $t('aqua.ui.grown.waiting_hint') }}
										</p>
									</div>
								</aside>
							</div>
						</div>
					</div>
				</div>
			</div>
			<GrownFishModal :fish="selectedFish" :isNotEditable="isNotEditable" @close="closeModal" />
			<modal
				class="modal__block modal__block--middle"
				name="GrownRejectModal"
				:width="560"
				:min-width="320"
				:adaptive="true"
				height="auto"
			>
				<div class="aq-page aq-modal">
					<h2 class="aq-modal__title">{{ $t('grown_journal.reject') }}</h2>
					<p class="aq-modal__text">{{ $t('aqua.ui.grown.reject_modal_hint') }}</p>
					<label class="aq-modal__field">
						<span class="aq-modal__label">{{ $t('grown_journal.reject_reason') }}</span>
						<textarea v-model="rejectReason" class="aq-input aq-input--area" rows="5" maxlength="1000"></textarea>
						<span class="aq-modal__counter" :class="{ 'is-short': rejectLength > 0 && rejectLength < 5 }">
							{{ $t('aqua.ui.grown.reject_counter', { count: rejectLength }) }}
						</span>
					</label>
					<div class="aq-modal__actions">
						<button type="button" class="aq-btn" @click="$modal.hide('GrownRejectModal')">{{ $t('button.cancel') }}</button>
						<button type="button" class="aq-btn aq-btn--danger-solid" :disabled="rejectLength < 5" @click.prevent="sendReject">
							{{ $t('grown_journal.reject') }}
						</button>
					</div>
				</div>
			</modal>
		</div>
	</div>
</template>

<style scoped>
.aq-shell {
	background: var(--aq-bg);
}
.aq-meta {
	color: var(--aq-muted);
	font-size: 14px;
}
.aq-layout {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 300px;
	align-items: start;
	gap: 24px;
}
.aq-layout__side {
	position: sticky;
	top: 16px;
}
.aq-alert__icon {
	flex-shrink: 0;
	margin-top: 1px;
	color: var(--aq-danger);
}
.aq-alert__text {
	margin: 4px 0 0;
	line-height: 1.5;
}
.aq-step {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	border-radius: 10px;
	background: var(--aq-primary-soft);
	color: var(--aq-primary);
	font-weight: 700;
}
.aq-facts {
	margin: 0;
}
.aq-facts dt {
	margin-bottom: 6px;
	color: var(--aq-ink-2);
	font-size: 13px;
	font-weight: 500;
}
.aq-facts dd {
	min-height: 44px;
	margin: 0;
	padding: 11px 0;
	border-bottom: 1px dashed var(--aq-border-strong);
	color: var(--aq-ink);
	font-size: 15px;
	font-weight: 500;
	word-break: break-word;
}
.aq-facts__wide {
	grid-column: 1 / -1;
}
.aq-muted-note {
	margin: 0;
	color: var(--aq-muted);
	font-size: 14px;
}
.aq-fishes {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
	gap: 14px;
}
.aq-fish {
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding: 14px 16px 16px;
	border: 1px solid var(--aq-border);
	border-radius: var(--aq-radius);
	background: var(--aq-surface);
	transition: border-color 0.2s var(--aq-ease), box-shadow 0.2s var(--aq-ease);
}
.aq-fish:hover {
	border-color: #b9d6f8;
	box-shadow: var(--aq-shadow-hover);
}
.aq-fish__head {
	display: flex;
	align-items: center;
	gap: 10px;
}
.aq-fish__num {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: var(--aq-ink);
	color: #fff;
	font-size: 13px;
	font-weight: 700;
}
.aq-fish__title {
	flex: 1;
	min-width: 0;
	color: var(--aq-ink);
	font-size: 15px;
	line-height: 1.35;
}
.aq-fish__progress {
	display: flex;
	flex-direction: column;
	gap: 6px;
}
.aq-fish__progress-label {
	display: flex;
	justify-content: space-between;
	gap: 8px;
	color: var(--aq-muted);
	font-size: 13px;
}
.aq-fish__progress-label strong {
	color: var(--aq-ink);
	font-size: 14px;
	white-space: nowrap;
}
.aq-fish__warn {
	color: var(--aq-warning);
	font-size: 12px;
	font-weight: 600;
}
.aq-fish__more {
	display: inline-flex;
	align-self: flex-start;
	align-items: center;
	gap: 4px;
	min-height: 36px;
	margin: -6px 0 -6px -8px;
	padding: 4px 8px;
	border-radius: 8px;
	color: var(--aq-primary);
	font-size: 14px;
	font-weight: 600;
	transition: background 0.2s var(--aq-ease);
}
.aq-fish__more:hover {
	background: var(--aq-primary-soft);
}
.aq-fish__more:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(21, 127, 238, 0.25);
}
.aq-bar {
	height: 6px;
	border-radius: 999px;
	background: #edf2f8;
	overflow: hidden;
}
.aq-bar span {
	display: block;
	height: 100%;
	border-radius: inherit;
	background: var(--aq-primary);
	transition: width 0.3s var(--aq-ease);
}
.aq-bar--over span {
	background: #e39a1b;
}
.aq-fish__stats {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 8px;
	margin: 0;
	padding-top: 12px;
	border-top: 1px solid var(--aq-border);
}
.aq-fish__stats dt {
	color: var(--aq-muted);
	font-size: 12px;
	font-weight: 500;
}
.aq-fish__stats dd {
	margin: 2px 0 0;
	color: var(--aq-ink);
	font-size: 15px;
	font-weight: 600;
}
.aq-btn--sm {
	min-height: 36px;
	padding: 6px 12px;
	font-size: 14px;
}
.aq-btn--danger-outline {
	border-color: #f3b4af;
	color: var(--aq-danger);
}
.aq-btn--danger-outline:hover {
	border-color: var(--aq-danger);
	background: var(--aq-danger-soft);
	color: var(--aq-danger);
}
.aq-btn--danger-solid {
	border-color: var(--aq-danger);
	background: var(--aq-danger);
	color: #fff;
}
.aq-btn--danger-solid:hover {
	border-color: #b9271d;
	background: #b9271d;
	color: #fff;
}
.aq-panel {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 20px;
}
.aq-panel ::v-deep .aq-kpi {
	padding: 14px 16px;
	box-shadow: none;
}
.aq-panel ::v-deep .aq-kpi__value {
	font-size: 24px;
}
.aq-panel__status {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 10px;
}
.aq-panel__label {
	color: var(--aq-ink-2);
	font-size: 14px;
	font-weight: 600;
}
.aq-panel__hint {
	margin: 0;
	color: var(--aq-muted);
	font-size: 13px;
	line-height: 1.45;
}
.aq-timeline {
	width: 100%;
	margin: 0;
	padding: 0;
	list-style: none;
}
.aq-timeline li {
	display: flex;
	flex-direction: column;
	padding: 6px 0 6px 14px;
	border-left: 2px solid var(--aq-border);
	font-size: 13px;
}
.aq-timeline span {
	color: var(--aq-muted);
}
.aq-timeline strong {
	color: var(--aq-ink);
	font-weight: 600;
}
.aq-panel__actions {
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding-top: 14px;
	border-top: 1px solid var(--aq-border);
}
.aq-modal {
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding: 28px;
	background: var(--aq-surface);
	text-align: left;
}
.aq-modal__title {
	margin: 0;
	font-size: 20px;
	font-weight: 700;
	color: var(--aq-ink);
}
.aq-modal__text {
	margin: 0;
	color: var(--aq-muted);
	font-size: 14px;
	line-height: 1.5;
}
.aq-modal__field {
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin: 0;
}
.aq-modal__label {
	color: var(--aq-ink-2);
	font-size: 13px;
	font-weight: 500;
}
.aq-modal__counter {
	align-self: flex-end;
	color: var(--aq-muted);
	font-size: 12px;
}
.aq-modal__counter.is-short {
	color: var(--aq-danger);
}
.aq-input--area {
	min-height: 120px;
	resize: vertical;
	line-height: 1.5;
}
.aq-modal__actions {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
}
@media (max-width: 1280px) {
	.aq-layout {
		grid-template-columns: minmax(0, 1fr);
	}
	.aq-layout__side {
		position: static;
		order: -1;
	}
	.aq-panel {
		flex-flow: row wrap;
		align-items: flex-start;
	}
	.aq-panel > * {
		flex: 1 1 220px;
	}
	.aq-panel__actions {
		flex-basis: 100%;
		flex-direction: row;
		flex-wrap: wrap;
		align-items: center;
	}
	.aq-panel__actions .aq-btn--block {
		width: auto;
		flex: 1 1 180px;
	}
}
@media (max-width: 768px) {
	.aq-card__body {
		padding: 16px;
	}
	.aq-fish__stats {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}
</style>
